// src/components/Moderation.js — Signal moderation with a reason and a right of
// appeal (25 Oct 2026; backend #203). Three small pieces:
//
//   HideButton   — a channel moderator hides a message; the reason is required
//                  and the author will see it.
//   NoticesPanel — the author's side: in-app notices for Signal, including
//                  "a message of yours was hidden" with the reason, and the
//                  one-time appeal form.
//   AppealsPanel — the other moderators' queue: read the appeal, the original
//                  text and the reason, then uphold or deny with a note. The
//                  backend refuses the moderator who hid it.
//
// The rule from the market review: AI or a moderator may flag; a human decides;
// the decision and its reason are visible to the author; the author can appeal.

import { useState, useEffect, useCallback } from 'react';
import { API_BASE as API } from '../utils/apiBase';

const C = { accent: '#14b8a6', text: '#d4d4d8', dim: '#a1a1aa', muted: '#52525b', warn: '#f59e0b', bad: '#f87171', ok: '#34d399' };
const box = { background: 'rgba(24,24,27,0.9)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '12px 14px', marginBottom: 10 };
const btn = (color = C.accent) => ({ padding: '6px 12px', borderRadius: 8, border: `1px solid ${color}55`, background: `${color}14`, color, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" });
const ta = { width: '100%', minHeight: 64, padding: 10, borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.3)', color: C.text, fontSize: 13, fontFamily: "'Inter', sans-serif", resize: 'vertical', boxSizing: 'border-box' };

async function call(path, headers, opts = {}) {
  const res = await fetch(`${API}${path}`, { ...opts, headers: { ...headers(), ...(opts.headers || {}) } });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.success === false) throw new Error(d.message || `HTTP ${res.status}`);
  return d;
}

// ── Hide (moderator) ─────────────────────────────────────────────────────────
export function HideButton({ msg, headers, onHidden }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  async function hide() {
    setBusy(true); setErr('');
    try { await call(`/api/signal/messages/${msg._id}/hide`, headers, { method: 'POST', body: JSON.stringify({ reason: reason.trim() }) }); setOpen(false); onHidden && onHidden(msg._id); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  }
  if (!open) return <button onClick={() => setOpen(true)} title="Hide this message (the author will see your reason)" style={{ ...btn(C.muted), padding: '1px 8px', fontSize: 10 }}>Hide</button>;
  return (
    <div style={{ ...box, marginTop: 6 }}>
      <div style={{ fontSize: 12, color: C.text, marginBottom: 6 }}>Why are you hiding this? <span style={{ color: C.muted }}>The author sees exactly this, and can appeal to another moderator.</span></div>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="At least 10 characters, e.g. 'Advertising, which this channel doesn't allow'" style={ta} autoFocus />
      {err && <div style={{ fontSize: 11, color: C.bad, marginTop: 4 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button onClick={hide} disabled={busy || reason.trim().length < 10} style={{ ...btn(C.warn), opacity: reason.trim().length < 10 ? 0.5 : 1 }}>Hide with this reason</button>
        <button onClick={() => setOpen(false)} style={btn(C.muted)}>Cancel</button>
      </div>
    </div>
  );
}

// ── Notices (author) ─────────────────────────────────────────────────────────
export function NoticesPanel({ headers, onClose }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { const d = await call('/api/notifications?app=signal&limit=30', headers); setItems(d.notifications || []); }
    catch (e) { setErr(e.message); setItems([]); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { // mark read on open; the backend only touches the caller's own rows
    call('/api/notifications/read-all?app=signal', headers, { method: 'POST' }).catch(() => {});
  }, [headers]);

  return (
    <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.04)', maxHeight: '50vh', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Notices</div>
        <button onClick={onClose} style={btn(C.muted)}>Close</button>
      </div>
      {items === null && <div style={{ fontSize: 12, color: C.muted }}>Loading…</div>}
      {err && <div style={{ fontSize: 12, color: C.bad }}>{err}</div>}
      {items && items.length === 0 && !err && <div style={{ fontSize: 12, color: C.muted }}>Nothing here. Signal only writes to you when something real happened.</div>}
      {items && items.map((n) => <Notice key={n._id} n={n} headers={headers} />)}
    </div>
  );
}

function Notice({ n, headers }) {
  const canAppeal = n.data && n.data.canAppeal && n.data.messageId;
  const [mod, setMod] = useState(null);     // moderation record for a hidden message
  const [text, setText] = useState('');
  const [state, setState] = useState('');   // '', 'sent', or an error
  useEffect(() => {
    if (!canAppeal) return;
    call(`/api/signal/messages/${n.data.messageId}/moderation`, headers).then((d) => setMod(d.data)).catch(() => setMod({ gone: true }));
  }, [canAppeal, n.data, headers]);
  async function appeal() {
    try { await call(`/api/signal/messages/${n.data.messageId}/appeal`, headers, { method: 'POST', body: JSON.stringify({ text: text.trim() }) }); setState('sent'); }
    catch (e) { setState(e.message); }
  }
  const when = new Date(n.createdAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const appealed = mod && mod.appeal && mod.appeal.submitted;
  return (
    <div style={box}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{n.title}</div>
        <div style={{ fontSize: 10, color: C.muted, flexShrink: 0 }}>{when}</div>
      </div>
      <div style={{ fontSize: 12, color: C.dim, marginTop: 4, lineHeight: 1.5 }}>{n.message}</div>
      {canAppeal && mod && !mod.gone && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          {mod.snapshot && <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic', marginBottom: 6 }}>Your message: “{mod.snapshot}”</div>}
          <div style={{ fontSize: 12, color: C.text }}>Reason given: <span style={{ color: C.warn }}>{mod.reason}</span></div>
          {appealed || state === 'sent' ? (
            <div style={{ fontSize: 12, color: C.dim, marginTop: 6 }}>
              {mod.appeal?.appealStatus === 'approved' || !mod.hidden ? <span style={{ color: C.ok }}>Appeal upheld — your message is visible again.</span>
                : mod.appeal?.appealStatus === 'denied' ? <span>Appeal reviewed; the decision stands.{mod.appeal.reviewNotes ? ` Note: ${mod.appeal.reviewNotes}` : ''}</span>
                : <span>Appeal sent. A different moderator will review it — you'll get a notice either way.</span>}
            </div>
          ) : (
            <div style={{ marginTop: 8 }}>
              <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Why was this decision wrong? (at least 10 characters)" style={ta} />
              {state && state !== 'sent' && <div style={{ fontSize: 11, color: C.bad, marginTop: 4 }}>{state}</div>}
              <button onClick={appeal} disabled={text.trim().length < 10} style={{ ...btn(), marginTop: 6, opacity: text.trim().length < 10 ? 0.5 : 1 }}>Appeal this decision</button>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>One appeal per message. The moderator who hid it cannot decide it.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Appeals (other moderators) ───────────────────────────────────────────────
export function AppealsPanel({ headers, onClose }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { const d = await call('/api/signal/appeals', headers); setItems(d.data || []); } catch (e) { setErr(e.message); setItems([]); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);
  return (
    <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.04)', maxHeight: '50vh', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Appeals waiting on a decision</div>
        <button onClick={onClose} style={btn(C.muted)}>Close</button>
      </div>
      {items === null && <div style={{ fontSize: 12, color: C.muted }}>Loading…</div>}
      {err && <div style={{ fontSize: 12, color: C.bad }}>{err}</div>}
      {items && items.length === 0 && !err && <div style={{ fontSize: 12, color: C.muted }}>No pending appeals in channels you moderate.</div>}
      {items && items.map((a) => <Appeal key={a._id} a={a} headers={headers} onDone={load} />)}
    </div>
  );
}

function Appeal({ a, headers, onDone }) {
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState('');
  async function decide(decision) {
    setErr('');
    try { await call(`/api/signal/appeals/${a._id}/decide`, headers, { method: 'POST', body: JSON.stringify({ decision, notes: notes.trim() }) }); onDone(); }
    catch (e) { setErr(e.message); }
  }
  return (
    <div style={box}>
      <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic' }}>Hidden message: “{a.removedContent?.contentSnapshot}”</div>
      <div style={{ fontSize: 12, color: C.text, marginTop: 6 }}>Hidden because: <span style={{ color: C.warn }}>{a.reason}</span></div>
      <div style={{ fontSize: 12, color: C.text, marginTop: 6 }}>The author says: <span style={{ color: C.dim }}>{a.appeal?.appealText}</span></div>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="A note for the author (optional, they will see it)" style={{ ...ta, minHeight: 48, marginTop: 8 }} />
      {err && <div style={{ fontSize: 11, color: C.bad, marginTop: 4 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button onClick={() => decide('approved')} style={btn(C.ok)}>Uphold appeal — show the message</button>
        <button onClick={() => decide('denied')} style={btn(C.bad)}>Decision stands</button>
      </div>
    </div>
  );
}
